import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { Form } from '@/components/forms';
import { Scanner } from '@/components/scanner';
import { useLayoutContext } from '@/contexts';
import { PrimaryLayout } from '@/layouts/primary';
import { Drawer, Select, Textarea, TextInput } from '@mantine/core';
import { IconScan, IconX } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { useCreateSalesforceCaseMutation } from './graphql.generated';
import { issueTypeOptions, ReportIssueFormValues, validationSchema } from './utils';

export const ReportIssuePage = () => {
	const [createSupportCase, { loading }] = useCreateSalesforceCaseMutation();
	const [isScannerOpen, setIsScannerOpen] = useState(false);
	const { isDesktop } = useLayoutContext();
	const navigate = useNavigate();
	const closeButton = useMemo(
		() => (
			<CompactButton leftSection={<IconX />} onClick={() => navigate({ to: '/' })}>
				Close
			</CompactButton>
		),
		[navigate]
	);

	const initialValues: ReportIssueFormValues = {
		typeOfIssue: undefined,
		comments: '',
		code: '',
	};

	const onSubmit = useCallback(
		async (values: ReportIssueFormValues) => {
			let description = '';

			if (values.code) description += `Code: ${values.code}`;
			if (values.comments) {
				if (description) description += '\n\n';
				description += `Comments: ${values.comments}`;
			}

			await createSupportCase({
				variables: {
					createSalesforceCaseInput: {
						reason: values.typeOfIssue ?? '',
						subject: values.typeOfIssue ?? '',
						description,
					},
				},
			});
			navigate({ to: '/help' });
		},
		[createSupportCase, navigate]
	);

	const formRef = useRef<FormikProps<ReportIssueFormValues>>(null);
	const triggerSubmit = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	const handleScanCode = useCallback((code: string) => {
		// Set the scanned code in the form
		if (formRef.current) {
			formRef.current.setFieldValue('code', code);
		}
		// Close the scanner modal
		setIsScannerOpen(false);
	}, []);

	return (
		<PrimaryLayout
			header={{ title: 'Report an Issue', children: closeButton }}
			footer={
				isScannerOpen
					? undefined
					: {
							children: (
								<StandardButton onClick={triggerSubmit} loading={loading}>
									Submit
								</StandardButton>
							),
						}
			}
		>
			<ContentPanel>
				<CustomContentSection
					location={ContentSectionLocation.ReportIssue}
					placement={ContentSectionPlacement.Top}
				/>
				<Form<ReportIssueFormValues>
					initialValues={initialValues}
					onSubmit={onSubmit}
					innerRef={formRef}
					validationSchema={validationSchema}
				>
					{({ getFieldProps, errors, touched, setFieldValue, values }) => (
						<>
							<Select
								label="Type of Issue"
								placeholder="Pease select..."
								data={issueTypeOptions}
								value={values.typeOfIssue}
								onChange={(value) => setFieldValue('typeOfIssue', value)}
								error={touched.typeOfIssue && errors.typeOfIssue}
								mb="md"
								withScrollArea={false}
							/>

							<TextInput
								label="Code"
								placeholder="Scan or enter a code that isn't working"
								{...getFieldProps('code')}
								mb="md"
								error={touched.code && errors.code}
								rightSection={
									<IconScan
										size={16}
										style={{ cursor: 'pointer' }}
										onClick={() => setIsScannerOpen(true)}
									/>
								}
								styles={{
									label: {
										fontFamily: 'DM Sans',
										fontWeight: '700',
										fontSize: '16px',
									},
								}}
							/>

							{/* Scanner Drawer */}
							<Drawer
								opened={isScannerOpen}
								onClose={() => setIsScannerOpen(false)}
								position="bottom"
								size="100%"
								withCloseButton
								title="Scan Code"
								styles={{
									close: {
										color: 'black',
									},
								}}
							>
								<Scanner onScan={handleScanCode} roundedCorners />
							</Drawer>

							<Textarea
								label="Comments"
								placeholder="Enter any additional comments"
								{...getFieldProps('comments')}
								error={touched.comments && errors.comments}
								mb="md"
								rows={14}
							/>

							{isDesktop && (
								<StandardButton onClick={triggerSubmit} loading={loading}>
									Submit
								</StandardButton>
							)}
						</>
					)}
				</Form>
				<CustomContentSection
					location={ContentSectionLocation.ReportIssue}
					placement={ContentSectionPlacement.Middle}
				/>
				<CustomContentSection
					location={ContentSectionLocation.ReportIssue}
					placement={ContentSectionPlacement.Bottom}
				/>
			</ContentPanel>
		</PrimaryLayout>
	);
};
